import {
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  useTheme,
} from '@mui/material';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { DateTime } from 'luxon';
import { useCallback, useMemo } from 'react';
import { getLevelTableColumnId, LevelTableAnyColumn, toColumnDefs } from 'redesign';
import { getDateColumns } from './hooks/get-date-columns';
import { CalendarGridProps, CalendarGridRow } from './calendar-grid.types';

export const CalendarGrid = <
  TableColumns extends CalendarGridRow<DateData>,
  DateData extends object[],
>(
  gridOpts: CalendarGridProps<TableColumns, DateData>
) => {
  const theme = useTheme();
  const { mode, startDate, cellFn, headerFn, onScroll } = gridOpts;

  const dateColumns = useMemo(
    () => getDateColumns<TableColumns, DateData>(mode, startDate, cellFn, headerFn),
    [mode, startDate, cellFn, headerFn]
  );
  const isCurrentDate = useCallback(
    (columnId: string) =>
      startDate.month === DateTime.now().month &&
      startDate.year === DateTime.now().year &&
      columnId == `DAY_${DateTime.now().day}`,
    [startDate]
  );
  const allColumns = useMemo(() => {
    const columns: LevelTableAnyColumn<TableColumns>[] = [...gridOpts.columns, ...dateColumns];
    return columns;
  }, [gridOpts.columns, dateColumns]);
  const columnDefs = useMemo(() => toColumnDefs(allColumns, {}), [allColumns]);
  const data = useMemo(() => [...gridOpts.data], [gridOpts.data]);

  const table = useReactTable({
    data,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <StyledTableWrapper onScroll={onScroll}>
      <StyledTable stickyHeader={true}>
        <colgroup>
          {allColumns.map((column) => {
            const columnId = getLevelTableColumnId(column);
            return (
              <StyledCurrentDateColumn
                key={columnId}
                width={column.width}
                isCurrentDate={isCurrentDate(columnId)}
                theme={theme}
              />
            );
          })}
        </colgroup>
        <TableHead>
          {table.getHeaderGroups().map((headerGroup) => (
            <StyledHeaderRow key={headerGroup.headers.map((i) => i.id).join('__')}>
              {headerGroup.headers.map((header) => (
                <StyledHeaderCell
                  key={header.id}
                  theme={theme}
                  isCurrentDate={isCurrentDate(header.column.id)}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </StyledHeaderCell>
              ))}
            </StyledHeaderRow>
          ))}
        </TableHead>
        <TableBody>
          {table.getRowModel().rows.map((row) => {
            return (
              <StyledTableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <StyledDataCell key={`${row.id}__${cell.column.id}`}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </StyledDataCell>
                ))}
              </StyledTableRow>
            );
          })}
        </TableBody>
      </StyledTable>
    </StyledTableWrapper>
  );
};

const StyledTable = styled(Table)(({ theme }) => ({
  '& tbody tr:has(input[type="checkbox"]:checked)': {
    backgroundColor: theme.palette.lwPrimaryAccentLight?.[5] ?? 'white',
  },
  '& tbody tr:is(.row-group-before, .row-group-last, .row-group-single) td': {
    borderBottom: 'none',
    paddingBottom: '17px',
  },
  // styling of the 'tr' children is necessary, because border radius cannot be applied to a 'tr'
  '& tbody tr.row-group-first td:first-of-type': { borderTopLeftRadius: '16px' },
  '& tbody tr.row-group-first td:last-child': { borderTopRightRadius: '16px' },
  '& tbody tr.row-group-last td:first-of-type': { borderBottomLeftRadius: '16px' },
  '& tbody tr.row-group-last td:last-child': { borderBottomRightRadius: '16px' },
  '& tbody tr.row-group-single td:first-of-type': { borderRadius: '16px 0 0 16px' },
  '& tbody tr.row-group-single td:last-child': { borderRadius: '0 16px 16px 0' },
  '& tbody': {
    borderTop: `solid 2px ${theme.palette.lwPrimaryAccentDark?.[20]}`,
    borderBottom: `solid 2px ${theme.palette.lwPrimaryAccentDark?.[20]}`,
  },
  '& button.MuiButtonBase-root': {
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  tableLayout: 'fixed',
}));

const StyledHeaderCell = styled(TableCell)(
  ({ theme, isCurrentDate }: { theme: Theme; isCurrentDate: boolean }) => ({
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    border: '1px solid',
    borderColor: theme.palette.lwPrimaryAccentDark?.[10] ?? 'lightgray',
    backgroundColor: isCurrentDate ? theme.palette.lwPrimaryAccentLight?.[5] : undefined,
    textAlign: 'center',
  })
);

const StyledCurrentDateColumn = styled('col')(
  ({
    theme,
    width,
    isCurrentDate,
  }: {
    theme: Theme;
    width: string | number | undefined;
    isCurrentDate: boolean;
  }) => ({
    backgroundColor: isCurrentDate ? theme.palette.lwPrimaryAccentLight?.[5] : 'transparent',
    width,
  })
);

const StyledHeaderRow = styled(TableRow)({
  height: '30px',
});

const StyledTableRow = styled(TableRow)({
  height: '50px',
});

const StyledDataCell = styled(TableCell)(({ theme }) => ({
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  backgroundColor: undefined,
  padding: 0,
  border: '1px solid',
  borderColor: theme.palette.lwPrimaryAccentDark?.[10] ?? 'lightgray',
  height: '100%',
  verticalAlign: 'top',
}));

const StyledTableWrapper = styled(TableContainer)(() => ({
  height: '100%',
  overflow: 'auto',
  outline: 'none',
}));
